import OpenAI from "openai";

// 从环境变量获取配置
const API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY;

// 创建 OpenAI 客户端实例
const openai = new OpenAI({
  baseURL: 'https://api.deepseek.com/v1',
  apiKey: API_KEY,
  timeout: 30000,
  dangerouslyAllowBrowser: true, // 允许在浏览器中使用
  defaultHeaders: {
    'Content-Type': 'application/json',
  }
});

interface ParsedProductInfo {
  title: string;
  description: string;
  tags: string[];
  sizes: string[];
  productCode: string;
  category: string;
  comparison: {
    original: {
      material: string;
      craftsmanship: string;
    };
    replica: {
      material: string;
      craftsmanship: string;
    };
  };
}

function generateParserPrompt(text: string): string {
  return `你是一个专业的商品信息提取助手。请从以下商品描述文本中提取关键信息，并按照指定格式返回JSON数据：

需要提取的信息：
1. 标题：提取主要商品名称，去除促销文案
2. 描述：提取商品详细描述，包括材质、工艺等信息
3. 标签：提取关键词，如品牌、系列、类型等（至少3个标签）
4. 尺码：提取所有可选尺码
5. 货号：提取商品货号（如果有）
6. 分类：判断商品类别（sneakers/clothing）
7. 对比信息：
   - 原版材质和工艺特点
   - 复刻材质和工艺特点

请将提取的信息格式化为以下JSON格式：
{
  "title": "商品标题",
  "description": "商品描述",
  "tags": ["标签1", "标签2", "标签3"],
  "sizes": ["尺码1", "尺码2"],
  "productCode": "货号",
  "category": "分类",
  "comparison": {
    "original": {
      "material": "原版材质描述",
      "craftsmanship": "原版工艺描述"
    },
    "replica": {
      "material": "复刻材质描述",
      "craftsmanship": "复刻工艺描述"
    }
  }
}

商品描述文本：
${text}

注意：
1. 如果某些信息在文本中没有明确提供，请根据上下文合理推断
2. 对于没有提供的信息，使用合适的默认值
3. 确保所有字段都有值，不要返回空字符串或null
4. 只返回JSON数据，不要包含任何其他解释性文字`;
}

export async function parseProductInfo(text: string): Promise<ParsedProductInfo> {
  const requestStartTime = Date.now();
  
  try {
    console.log('=== 开始解析商品信息 ===');
    console.log('输入文本:', text);
    console.log('当前时间:', new Date().toISOString());
    console.log('API配置:', {
      baseURL: openai.baseURL,
      hasApiKey: !!openai.apiKey,
      timeout: openai.timeout
    });

    const prompt = generateParserPrompt(text);

    try {
      const completion = await openai.chat.completions.create({
        model: "deepseek-chat",
        messages: [
          {
            role: "system",
            content: "你是一个专业的商品信息提取助手，只返回格式化的JSON数据，不需要任何解释或额外内容。"
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.1,
        max_tokens: 1000,
        response_format: { type: "json_object" }
      });

      const requestDuration = Date.now() - requestStartTime;
      console.log('请求耗时:', requestDuration, 'ms');

      if (!completion.choices?.[0]?.message?.content) {
        console.error('API返回数据不完整:', completion);
        throw new Error('API返回数据格式错误');
      }

      const parsedContent = completion.choices[0].message.content.trim();
      console.log('解析结果:', parsedContent);

      let parsedData;
      try {
        parsedData = JSON.parse(parsedContent);
        console.log('JSON解析成功，数据结构:', {
          hasTitle: !!parsedData.title,
          hasDescription: !!parsedData.description,
          tagsCount: parsedData.tags?.length,
          sizesCount: parsedData.sizes?.length,
          hasComparison: !!parsedData.comparison
        });
      } catch (e) {
        console.error('JSON解析失败:', e);
        console.error('问题内容:', parsedContent);
        throw new Error('返回数据格式错误，无法解析为JSON');
      }

      // 验证和规范化数据
      const result = {
        title: parsedData.title || '未知商品',
        description: parsedData.description || '暂无描述',
        tags: Array.isArray(parsedData.tags) ? parsedData.tags : ['未分类'],
        sizes: Array.isArray(parsedData.sizes) ? parsedData.sizes : [],
        productCode: parsedData.productCode || '',
        category: parsedData.category || 'sneakers',
        comparison: {
          original: {
            material: parsedData.comparison?.original?.material || '优质原厂材料',
            craftsmanship: parsedData.comparison?.original?.craftsmanship || '原厂工艺制作'
          },
          replica: {
            material: parsedData.comparison?.replica?.material || '高品质复刻材料',
            craftsmanship: parsedData.comparison?.replica?.craftsmanship || '精细复刻工艺'
          }
        }
      };

      console.log('=== 处理完成 ===');
      console.log('总耗时:', Date.now() - requestStartTime, 'ms');
      console.log('最终结果:', result);

      return result;
    } catch (error: unknown) {
      const errorTime = Date.now() - requestStartTime;
      if (error instanceof Error) {
        console.error('请求失败:', {
          errorType: error.name,
          errorMessage: error.message,
          timeElapsed: errorTime
        });
      }
      throw error;
    }
  } catch (error) {
    console.error('=== 解析过程失败 ===');
    console.error('总耗时:', Date.now() - requestStartTime, 'ms');
    console.error('错误详情:', {
      type: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
} 